import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { StateBadge } from '@/components/platform/StateBadge';
import { Checklist } from '@/components/ui/Checklist';
import { ConfirmActionDialog, type ImpactStatus } from '@/components/platform/ConfirmActionDialog';
import { useEnumLabel, useInstitutionTypeLabel } from '@/components/platform/useLabel';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import {
  ONBOARDING_TRANSITIONS,
  caseDisplayName,
  type OnboardingCaseDetail,
  type OnboardingReadiness,
  type OnboardingState,
} from '@/types/platform';
import { formatDateTime, formatRelative } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * PA-02, the case half: see what is outstanding, then move the case on.
 *
 * THE FIELD NAMES ARE CORE'S NOW. A case carries `legalName` and `caseReference`,
 * not `institutionName` and `reference`, and its outstanding work lives under
 * `actions` with a `title` and a `blocksTransition` flag. The old names were not a
 * cosmetic difference: the list normaliser required `institutionName` to be
 * present, so every case core returned was discarded and the pipeline read as
 * empty with no error anywhere.
 *
 * THE TRANSITION MAP IS MIRRORED FROM CORE RATHER THAN GUESSED. The previous
 * fallback was wrong in both directions — it hid REJECTED → DRAFT, which is how a
 * returning prospect keeps its reserved institution code, and it offered
 * TECHNICAL_CONFIGURATION → INFORMATION_REQUIRED, which core refuses. The server
 * still enforces the machine; this map only decides which buttons are worth
 * drawing.
 *
 * READINESS IS ASKED BEFORE THE MOVE, NOT DISCOVERED BY BEING REFUSED. The shape
 * of a transition is a client-side fact; whether a checklist stage is outstanding
 * or a blocking action is open is not. `GET :id/readiness` answers that, and the
 * dialog shows the answer, so "this is not allowed" arrives with the two things
 * standing in the way rather than as a 409 with no addressee.
 *
 * TICKING A CHECKLIST ITEM IS A PATCH BY ITS ID. It used to be a POST keyed by the
 * item's code, a route no service has ever mounted, so every tick 404'd and the box
 * sprang back citing a missing endpoint.
 *
 * Every transition asks for a reason, and the three that end a case ask for the
 * institution name to be typed. Progressing a case is a privileged act recorded
 * against a person's name; it should feel like one.
 */

/** Transitions a case does not come back from without a new case being opened. */
const ENDING_STATES: readonly OnboardingState[] = ['REJECTED', 'CLOSED', 'SUSPENDED'];

export function OnboardingCaseDetailPage() {
  const { caseId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const label = useEnumLabel();
  const typeLabel = useInstitutionTypeLabel();
  const locale = i18n.language;

  const fetcher = useCallback(() => platformApi.onboarding.getCase(caseId), [caseId]);
  const { state, reload, replace } = useAsyncData<OnboardingCaseDetail>(`case:${caseId}`, fetcher, caseId.length > 0);

  const [target, setTarget] = useState<OnboardingState | null>(null);
  const [readiness, setReadiness] = useState<OnboardingReadiness | null>(null);
  const [readinessStatus, setReadinessStatus] = useState<ImpactStatus>('loading');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [checklistBusy, setChecklistBusy] = useState<string | null>(null);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [checklistError, setChecklistError] = useState<string | null>(null);

  const detail = state.status === 'ready' ? state.data : null;

  // Asked when the dialog opens, for the state actually chosen. Asking on page
  // load would mean a request per candidate for a decision nobody has made yet.
  useEffect(() => {
    if (!target || !caseId) return;
    let active = true;
    setReadinessStatus('loading');
    setReadiness(null);

    platformApi.onboarding
      .readiness(caseId, target)
      .then((result) => {
        if (!active) return;
        setReadiness(result);
        setReadinessStatus('ready');
      })
      .catch(() => {
        // The readiness endpoint being down must not block the move. The dialog
        // degrades to an explicit acknowledgement rather than implying the way is
        // clear; the server refuses anyway if it is not.
        if (active) setReadinessStatus('unavailable');
      });

    return () => {
      active = false;
    };
  }, [target, caseId]);

  const runTransition = async (reason: string) => {
    if (!detail || !target) return;
    setSubmitting(true);
    setActionError(null);
    try {
      replace(await platformApi.onboarding.transition(detail.id, { targetState: target, reason }));
      setTarget(null);
    } catch (cause) {
      setActionError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Creates the institution this case exists to produce.
   *
   * Core has served this endpoint all along and nothing here called it, so no case
   * ever got an `institutionId` and the institution register stayed empty however many
   * cases reached ACTIVE. Bootstrapping is a one-way, one-time act — it creates a live
   * tenant, institution, campus and branch — so it is a deliberate button rather than a
   * side effect of a transition, and Core refuses a second one on the same case.
   */
  const bootstrapInstitution = async () => {
    if (!detail) return;
    setBootstrapping(true);
    setBootstrapError(null);
    try {
      await platformApi.structure.bootstrapInstitution({ onboardingCaseId: detail.id });
      // Refetched rather than patched locally: the case gains institutionId, which is
      // what turns the "open institution" link on and this button off.
      reload();
    } catch (cause) {
      setBootstrapError((cause as Error).message);
    } finally {
      setBootstrapping(false);
    }
  };

  const toggleChecklist = async (itemId: string, completed: boolean) => {
    if (!detail) return;
    setChecklistBusy(itemId);
    setChecklistError(null);
    try {
      // The endpoint returns the item, not the case. The case is refetched so the
      // progress count and the readiness of the next transition both move with it.
      await platformApi.onboarding.setChecklistItem(detail.id, itemId, completed);
      reload();
    } catch (cause) {
      // The box springs back because the change did not happen. Leaving it ticked
      // would show a case as further along than it is.
      setChecklistError((cause as Error).message);
    } finally {
      setChecklistBusy(null);
    }
  };

  return (
    <div className={styles.page}>
      <nav className={styles.breadcrumb} aria-label={t('platform.cases.breadcrumbLabel')}>
        <Link to="/admin/onboarding">{t('platform.cases.title')}</Link>
        <Icon name="chevronRight" size={13} />
        <span>{detail ? caseDisplayName(detail) : caseId}</span>
      </nav>

      <AsyncPanel state={state} onRetry={reload}>
        {(data) => {
          const transitions = ONBOARDING_TRANSITIONS[data.state] ?? [];
          const checklist = data.checklist ?? [];
          const done = checklist.filter((item) => item.completed).length;
          const openActions = (data.actions ?? []).filter((action) => action.status === 'OPEN');
          const name = caseDisplayName(data);

          return (
            <>
              <header className={styles.caseHeader}>
                <div>
                  <h1 className={styles.pageTitle}>{name}</h1>
                  <p className={styles.pageLead}>
                    {typeLabel(data.institutionType)}
                    {data.institutionCode ? ` · ${data.institutionCode}` : ''}
                    {data.caseReference ? ` · ${data.caseReference}` : ''}
                  </p>
                </div>
                <div className={styles.caseHeaderAside}>
                  <StateBadge kind="onboarding" value={data.state} />
                  <span className={styles.caseUpdated}>
                    {t('platform.case.updated', { when: formatRelative(data.updatedAt, locale) })}
                  </span>
                </div>
              </header>

              <Panel
                title={t('platform.case.progress.title')}
                icon="switch"
                meta={t('platform.case.progress.meta', { state: label('state.onboarding', data.state) })}
                emphasis="attention"
              >
                {transitions.length === 0 ? (
                  <p className={styles.sectionEmpty} role="status">
                    <Icon name="check" size={15} aria-hidden="true" />
                    {t('platform.case.progress.none')}
                  </p>
                ) : (
                  <>
                    <p className={styles.fieldHint}>{t('platform.case.progress.hint')}</p>
                    <div className={styles.transitionRow}>
                      {transitions.map((next) => (
                        <button
                          key={next}
                          type="button"
                          className={ENDING_STATES.includes(next) ? styles.transitionBtnDanger : styles.transitionBtn}
                          onClick={() => {
                            setActionError(null);
                            setTarget(next);
                          }}
                        >
                          {label('state.onboarding', next)}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </Panel>

              <div className={styles.gridTwo}>
                <Panel
                  title={t('platform.case.outstanding.title')}
                  icon="warning"
                  meta={data.actions ? String(openActions.length) : undefined}
                  emphasis="attention"
                >
                  {!data.actions ? (
                    <p className={styles.sectionUnavailable} role="status">
                      {t('platform.dashboard.sectionUnavailable')}
                    </p>
                  ) : openActions.length === 0 ? (
                    <p className={styles.sectionEmpty} role="status">
                      <Icon name="check" size={15} aria-hidden="true" />
                      {t('platform.case.outstanding.empty')}
                    </p>
                  ) : (
                    <ul className={styles.rowList}>
                      {openActions.map((action) => (
                        <li key={action.id} className={styles.row}>
                          <div className={styles.rowMain}>
                            <p className={styles.rowTitle}>{action.title}</p>
                            {action.description && <p className={styles.rowDetail}>{action.description}</p>}
                            <p className={styles.rowMetaLine}>
                              {action.dueDate
                                ? t('platform.case.outstanding.due', {
                                    when: formatRelative(action.dueDate, locale),
                                  })
                                : t('platform.case.outstanding.noDue')}
                              {action.assignedToName ? ` · ${action.assignedToName}` : ''}
                            </p>
                          </div>
                          <div className={styles.rowAside}>
                            {/*
                              Blocking and advisory are different problems. One
                              stops the case moving; the other is a note.
                            */}
                            <span className={action.blocksTransition ? styles.dangerTag : styles.mutedTag}>
                              {action.blocksTransition
                                ? t('platform.case.outstanding.blocking')
                                : t('platform.case.outstanding.advisory')}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </Panel>

                <Panel
                  title={t('platform.case.checklist.title')}
                  icon="checklist"
                  meta={data.checklist ? t('platform.case.checklist.meta', { done, total: checklist.length }) : undefined}
                >
                  {!data.checklist ? (
                    <p className={styles.sectionUnavailable} role="status">
                      {t('platform.dashboard.sectionUnavailable')}
                    </p>
                  ) : checklist.length === 0 ? (
                    <p className={styles.sectionEmpty} role="status">
                      {t('platform.case.checklist.empty')}
                    </p>
                  ) : (
                    <>
                    <Checklist
                      idPrefix="case-checklist"
                      disabled={checklistBusy !== null}
                      error={checklistError ?? undefined}
                      items={checklist.map((item) => ({
                        value: item.id,
                        label: (
                          <span className={item.completed ? styles.checklistDone : undefined}>
                            {item.label}
                            {item.required && (
                              <span className={styles.requiredMark}> {t('platform.case.checklist.required')}</span>
                            )}
                          </span>
                        ),
                        meta: (
                          <>
                            {t(`platform.case.checklist.stage.${item.stage}`, { defaultValue: item.stage })}
                            {item.completed && item.completedBy && (
                              <>
                                {' · '}
                                {t('platform.case.checklist.completedBy', {
                                  who: item.completedBy,
                                  when: formatRelative(item.completedAt ?? undefined, locale),
                                })}
                              </>
                            )}
                          </>
                        ),
                      }))}
                      values={checklist.filter((item) => item.completed).map((item) => item.id)}
                      onToggle={(itemId, checked) => {
                        void toggleChecklist(itemId, checked);
                      }}
                    />
                    </>
                  )}
                </Panel>
              </div>

              <Panel title={t('platform.case.history.title')} icon="history">
                {!data.history ? (
                  <p className={styles.sectionUnavailable} role="status">
                    {t('platform.dashboard.sectionUnavailable')}
                  </p>
                ) : data.history.length === 0 ? (
                  <p className={styles.sectionEmpty} role="status">
                    {t('platform.case.history.empty')}
                  </p>
                ) : (
                  <ol className={styles.timeline}>
                    {data.history.map((entry) => (
                      <li key={entry.id} className={styles.timelineItem}>
                        <span className={styles.timelineDot} aria-hidden="true" />
                        <div className={styles.rowMain}>
                          <p className={styles.rowTitle}>
                            {entry.fromState
                              ? t('platform.case.history.moved', {
                                  from: label('state.onboarding', entry.fromState),
                                  to: label('state.onboarding', entry.toState),
                                })
                              : t('platform.case.history.opened', {
                                  state: label('state.onboarding', entry.toState),
                                })}
                          </p>
                          <p className={styles.rowMetaLine}>
                            {entry.actorName ?? entry.actorId ?? '—'} · {formatDateTime(entry.occurredAt, locale)}
                          </p>
                          {entry.reason && (
                            <p className={styles.rowDetail}>
                              {t('platform.case.history.reason', { reason: entry.reason })}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </Panel>

              {data.institutionId ? (
                <p className={styles.footnote}>
                  <Icon name="institution" size={14} />
                  <Link to={`/admin/institutions/${encodeURIComponent(data.institutionId)}`}>
                    {t('platform.case.openInstitution')}
                  </Link>
                </p>
              ) : (
                /*
                 * No institution yet. Shown for every unbootstrapped case, not only the
                 * ones far enough along to act on it, because the absence is the thing
                 * worth knowing: a case can otherwise be worked all the way to ACTIVE
                 * and leave nothing in the register, which is exactly what happened
                 * before this existed. Core decides whether the button may fire — it
                 * accepts TECHNICAL_CONFIGURATION, READY_FOR_ACTIVATION, and ACTIVE as
                 * a repair for cases stranded by that older behaviour — so the refusal
                 * and its reason come from the one place that can be right about it.
                 */
                <Panel title={t('platform.case.institution.title')}>
                  <p className={styles.rowDetail}>{t('platform.case.institution.absent')}</p>
                  <button
                    type="button"
                    className={styles.transitionBtn}
                    onClick={bootstrapInstitution}
                    disabled={bootstrapping}
                  >
                    <Icon name="institution" size={14} />
                    {bootstrapping
                      ? t('platform.case.institution.creating')
                      : t('platform.case.institution.create')}
                  </button>
                  {bootstrapError && (
                    <p className={styles.formError} role="alert">
                      {bootstrapError}
                    </p>
                  )}
                </Panel>
              )}

              <ConfirmActionDialog
                open={target !== null}
                title={t('platform.case.confirm.title', { state: label('state.onboarding', target ?? undefined) })}
                intro={t('platform.case.confirm.intro', {
                  name,
                  from: label('state.onboarding', data.state),
                  to: label('state.onboarding', target ?? undefined),
                })}
                tone={target && ENDING_STATES.includes(target) ? 'critical' : 'warning'}
                impactStatus={readinessStatus}
                impact={readiness ? <ReadinessSummary readiness={readiness} /> : null}
                confirmPhrase={target && ENDING_STATES.includes(target) ? name : undefined}
                confirmLabel={t('platform.case.confirm.confirm')}
                submitting={submitting}
                error={actionError}
                onCancel={() => setTarget(null)}
                onConfirm={runTransition}
              />
            </>
          );
        }}
      </AsyncPanel>
    </div>
  );
}

function ReadinessSummary({ readiness }: { readiness: OnboardingReadiness }) {
  const { t } = useTranslation();

  return (
    <>
      <ul className={styles.impactList}>
        <li>{t('platform.case.confirm.impactAudit')}</li>
        {readiness.transitionAllowed ? (
          <li>{t('platform.case.readiness.allowed')}</li>
        ) : (
          // Shown, not hidden, and the button is left enabled: the server is the
          // authority and may know something has changed since this was asked.
          // What the operator must not get is a refusal with no addressee.
          <li>{t('platform.case.readiness.blocked')}</li>
        )}
      </ul>

      {readiness.incompleteStages.length > 0 && (
        <ul className={styles.impactListWarning}>
          <li>
            {t('platform.case.readiness.incompleteStages', {
              stages: readiness.incompleteStages
                .map((stage) => t(`platform.case.checklist.stage.${stage}`, { defaultValue: stage }))
                .join(', '),
            })}
          </li>
        </ul>
      )}

      {readiness.blockingActions.length > 0 && (
        <ul className={styles.impactListWarning}>
          {readiness.blockingActions.map((action) => (
            <li key={action.id}>{t('platform.case.readiness.blockingAction', { title: action.title })}</li>
          ))}
        </ul>
      )}
    </>
  );
}
